import { isNull } from "@/util";
import { StatisticsItem, TransactionItem } from "@/domain/types/transactions_query";
import * as util from "@/util";

export interface AmountStatistic {
  [bankKey: string]: CardStatistics;
}

export interface CardStatistics {
  name: string;
  cardTypes: { [cardKey: string]: number };
}

export interface StatisticsResult {
  count: number;
  amount: number;
  error: boolean;
  cardTypeSummary: AmountStatistic;
  cardTypeSummaryQuantity: AmountStatistic;
  headers: string[];
}

export function calcTransactionTotals(items: TransactionItem[]) {
  const result = {
    count: 0,
    amount: 0,
    error: false
  };
  if (!util.isEmpty(items)) {
    result.count = items.length;
    items.forEach((txn) => {
      if (util.isNull(txn.amountWithSign)) {
        result.error = true;
      } else {
        const val = util.toNumber(txn.amountWithSign);
        if (val) {
          result.amount += val;
        }
      }
    });
  }
  return result;
}

export function calcTransactionStatsV2(items: TransactionItem[], opts: { forBank: boolean }): StatisticsResult {
  const result: StatisticsResult = {
    count: 0,
    amount: 0,
    error: false,
    cardTypeSummary: {},
    cardTypeSummaryQuantity: {},
    headers: []
  };
  const cardTypeSummary = new Array<StatisticsItem>();
  let statAmount: AmountStatistic = {};
  let statQuantity: AmountStatistic = {};
  const tableHeader: string[] = [];

  initialCalculation(items, cardTypeSummary, result);

  cardTypeSummary.forEach((item: StatisticsItem) => {
    if (opts.forBank) {
      insertStatistic(tableHeader, statAmount, item.bankName, item.cardTypeCode, item.amount);
      insertStatistic(tableHeader, statQuantity, item.bankName, item.cardTypeCode, 1);
    } else {
      insertStatistic(tableHeader, statAmount, item.branchID + "-" + item.branchName, item.cardTypeCode, item.amount);
      insertStatistic(tableHeader, statQuantity, item.branchID + "-" + item.branchName, item.cardTypeCode, 1);
    }
  });

  addRowTotal(statAmount);
  addRowTotal(statQuantity);

  statAmount = sortObject(statAmount);
  statQuantity = sortObject(statQuantity);

  calculateTotals(tableHeader, statAmount, statQuantity);

  // Update variables in result
  result.headers = tableHeader;
  result.cardTypeSummary = statAmount;
  result.cardTypeSummaryQuantity = statQuantity;
  return result;
}

function initialCalculation(items: TransactionItem[], cardTypeSummary: StatisticsItem[], result: StatisticsResult) {
  if (util.isEmpty(items)) return;
  result.count = items.length;
  for (const transaction of items) {
    cardTypeSummary.push({
      bankName: transaction.bankName || "",
      branchID: transaction.branchID || "",
      branchName: transaction.branchName || "",
      cardTypeCode: transaction.cardTypeCode || "",
      amount: util.toNumber(transaction.amountWithSign)
    });
    if (isNull(transaction.amountWithSign)) {
      result.error = true;
    } else {
      const val = util.toNumber(transaction.amountWithSign);
      if (val) {
        result.amount += val;
      }
    }
  }
}

function calculateTotals(tableHeader: string[], statAmount: AmountStatistic, statQuantity: AmountStatistic) {
  tableHeader.sort((a, b) => a.localeCompare(b));
  tableHeader.push("TOTAL");

  // ADD ROW TOTALS
  const quantityTotal: CardStatistics = { name: "TOTAL", cardTypes: {} };
  const amountTotal: CardStatistics = { name: "TOTAL", cardTypes: {} };

  for (const bank in statAmount) {
    if (bank == "name") continue;
    for (const type in statAmount[bank].cardTypes) {
      const amount = statAmount[bank]?.cardTypes[type];
      if (typeof amount !== "number") continue;
      if (amountTotal.cardTypes[type]) {
        amountTotal.cardTypes[type] += amount;
        quantityTotal.cardTypes[type] += statQuantity[bank].cardTypes[type];
      } else {
        amountTotal.cardTypes[type] = amount;
        quantityTotal.cardTypes[type] = statQuantity[bank].cardTypes[type];
      }
    }
  }

  //Include Totals To object
  statAmount.TOTAL = amountTotal;
  statQuantity.TOTAL = quantityTotal;
}

function insertStatistic(tableHeader: string[], stat: AmountStatistic, bank: string, type: string, amount: number) {
  if (tableHeader.indexOf(type) === -1) tableHeader.push(type);
  const bankItem = stat[bank];
  if (bankItem?.cardTypes[type]) {
    bankItem.cardTypes[type] = bankItem.cardTypes[type] + amount;
  } else if (bankItem) {
    bankItem.cardTypes[type] = amount;
  } else {
    stat[bank] = {
      name: bank,
      cardTypes: {
        [type]: amount
      }
    };
  }
}

// Add row total (by bank)
function addRowTotal(arr: AmountStatistic) {
  for (const bank in arr) {
    let sum = 0;
    for (const key in arr[bank].cardTypes) {
      if (typeof arr[bank].cardTypes[key] === "number") {
        sum += arr[bank].cardTypes[key];
      }
    }
    arr[bank].cardTypes["TOTAL"] = sum;
  }
}
// Sort Object
function sortObject(array: AmountStatistic): AmountStatistic {
  return Object.keys(array)
    .sort((a, b) => a.localeCompare(b))
    .reduce((obj: AmountStatistic, key) => {
      obj[key] = array[key];
      return obj;
    }, {} as AmountStatistic);
}
